import { gsap } from "gsap"
import { BackgroundElements } from "./background-elements"
import { ProgressBar } from "./progress-bar"
import { isTablet } from "./utils"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

declare var $

interface ScrollRange {
  start: number,
  snap: number,
  end: number
}

export class GsapWrapper {
  el: HTMLElement
  sections: typeof $
  sectionCount: number
  tl: gsap.core.Timeline
  tlDirection: number
  scrollArray: Array<ScrollRange>
  resizeIID: number

  constructor(el) {
    this.el = el
    this.sections = $('.section', this.el)
    this.sectionCount = this.sections.length
    this.tlDirection = 0
    this.init()
  }

  init() {
    $(() => {this.ready()})
  }

  ready() {
    if ( isTablet() ) {
      // Divide halves
      this.splitHalves()
      this.sections = $('.section', this.el)
      this.sectionCount = this.sections.length
    }

    $(this.el).height( $(window).height() * this.sectionCount * 1.25 )
    this.initSections()
    new ProgressBar( this.el, this.sectionCount )
    new BackgroundElements( this.el, this.sectionCount )
    window.scrollTo( 0, $(window).height() * 0.625 )
    $('body') 
      .on('click', '[href="#top"]', (e) => {
        e.preventDefault()
        window.scrollTo( 0, $(window).height() * 0.625 )
      })
  }

  /**
   * Main timeline
   */  

  initSections() {
    this.tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.5,
        snap: { 
          snapTo: this.scrollSectionCenter(),
          delay: 0,
          duration: {min: 1, max: 1.5}
        },
        onUpdate: (self) => {
          this.tlDirection = self.direction
        }
      }
    })

    const frame = 1 / this.sectionCount
    let lastWasPink = false

    // Build timeline
    this.sections.each((i,el) => {
      this.tl
      this.tl.addLabel('section-'+i, frame * i)
      if ( lastWasPink !== $(el).hasClass('bg-pink') ) {
        this.tl.set('.chapter-background', { backgroundColor: $(el).hasClass('bg-pink')?'#F7E3EE':'#02193A' }, "section-"+i)
      }
      this.tl.set(el, { opacity: 1, pointerEvents: 'all' }, "section-"+i)
      this.tl.call( (el) => { this.videoEnter(el) }, [], "section-"+i)
      this.sectionEnter(el, i, frame)
      this.tl.call( (el) => { this.videoExit(el) }, [], "section-"+i+"+="+frame )
      if (!$(el).hasClass('anim-outtro')) {
        this.tl.set(el, { opacity: 0, pointerEvents: 'none' }, "section-"+i+"+="+frame)
      }
      lastWasPink = $(el).hasClass('bg-pink')
    })
  }

  scrollSectionCenter () {
    let returnValue:number[] = []
    const frame = 1 / this.sectionCount
    for(let i = 0; i < this.sectionCount; i++) {
      returnValue.push( (i+0.5) * frame )
    }
    return returnValue
  }

  sectionEnter(el, i, frame) {
    if ($(el).hasClass('anim-q2032')) { this.sectionQ2032(el, i, frame) }
    if ($(el).hasClass('anim-qrc'))   { this.sectionQrc(el, i, frame) }
    if ($(el).hasClass('anim-rez'))   { this.sectionRez(el, i, frame) }

    if ($(el).hasClass('anim-header'))  { this.sectionEnterHeader(el, i, frame); return }
    if ($(el).hasClass('anim-chapter')) { this.sectionEnterChapter(el, i, frame); return }
    if ($(el).hasClass('anim-halves'))  { this.sectionEnterHalves(el, i, frame); return }
    if ($(el).hasClass('anim-halves-split'))  { this.sectionEnterHalvesSplit(el, i, frame); return }
    if ($(el).hasClass('anim-major'))   { this.sectionEnterMajor(el, i, frame); return }
    if ($(el).hasClass('anim-beside'))  { this.sectionEnterBeside(el, i, frame); return }
    if ($(el).hasClass('anim-awards'))  { this.sectionEnterAwards(el, i, frame); return }
    if ($(el).hasClass('anim-photos'))  { this.sectionEnterPhotos(el, i, frame); return }
    if ($(el).hasClass('anim-logos'))   { this.sectionEnterLogos(el, i, frame); return }
    this.sectionEnterDefault(el, i, frame)
  }

  sectionEnterHeader(el, i, frame) {
    this.tl
      .call( (el) => { this.videoPlay(el) }, [], "section-"+i+'+='+(frame*0.25) )
      .set( $('.header-bg', el), { opacity: 0 }, 'section-'+i+'+='+(frame*0.55) )
      .to( $('video', el), { opacity: 0, duration: frame * 0.4 }, 'section-'+i+'+='+(frame*0.6) )
  }

  sectionEnterChapter(el, i, frame) {
    this.tl
      .from( $('.chapter-title', el), {opacity: 0, x:-100, y:100, duration: frame * 0.4 }, 'section-'+i )
      .from( $('.chapter-intro', el), {opacity: 0, x:-100, y:100, duration: frame * 0.4 }, 'section-'+i+'+='+(frame*0.1) )
      .to( $('.chapter-title, .chapter-intro', el), { opacity: 0, x:100, y:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )

    if ($('.anim-globe', el).length) {
      this.tl
        .from( $('.anim-globe', el), {opacity:0, duration: frame * 0.1 }, 'section-'+i )
        .from( $('.anim-globe', el), {xPercent:-120, yPercent:-25, rotate:-20, scale:5, duration: frame * 0.4 }, 'section-'+i )
    }
  }

  sectionEnterHalves(el, i, frame) {
    const leftHalf = $('.half', el).first()
    const rightHalf = $('.half', el).last()
    
    this.tl
      .from( $('.body-lg, .body-client', leftHalf), { opacity: 0, duration: frame * 0.1, stagger: frame * 0.05 }, 'section-'+i )
      .from( $('.body-lg, .body-client', rightHalf), { opacity: 0, duration: frame * 0.1, stagger: frame * 0.05 }, 'section-'+i+'+='+(frame*0.1) )      
      .from( $('.body-lg, .body-client', leftHalf), { y:'-5vh', duration: frame * 0.15 }, 'section-'+i+'+='+(frame*0.2) )
      .from( $('.body-lg, .body-client', rightHalf), { y:'5vh', duration: frame * 0.15 }, 'section-'+i+'+='+(frame*0.2) )
      .from( $('.media', leftHalf), { opacity: 0, x:-100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.2) )
      .from( $('.media', rightHalf), {opacity: 0, x:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.3) )

      .to( $('.media', leftHalf), {opacity: 0, x:-100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
      .to( $('.media', rightHalf), {opacity: 0, x:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
      .to( $('.body-lg, .body-client', el), { opacity: 0, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
  }

  sectionEnterHalvesSplit(el, i, frame) {
    this.tl
      .from( $('.body-lg, .body-client', el), { opacity: 0, duration: frame * 0.1, stagger: frame * 0.05 }, 'section-'+i )
      .from( $('.body-lg, .body-client', el), { y:'-5vh', duration: frame * 0.15 }, 'section-'+i+'+='+(frame*0.2) )
      .from( $('.media', el), { opacity: 0, x:-100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.2) )

      .to( $('.media', el), {opacity: 0, x:-100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
      .to( $('.body-lg, .body-client', el), { opacity: 0, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
  }

  sectionEnterMajor(el, i, frame) {
    this.tl
      .from( $('.major-moment-media .media', el), { opacity: 0, x:'50vh', duration: frame * 0.4 }, 'section-'+i)
      .from( $('.major-moment-content', el), { opacity: 0, x:-100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.2))
      .to( $('.major-moment-media .media', el), { opacity: 0, y:'-50vh', duration: frame * 0.4 }, 'section-'+i+'+='+(frame*0.6))
      .to( $('.major-moment-content', el), { opacity: 0, x:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8))
  }

  sectionEnterBeside(el, i, frame) {
    this.tl
      .from( $('.media', el), { y:'100vh', duration: frame * 0.4 }, 'section-'+i)
      .from( $('.content', el), { opacity: 0, x:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.2))
      .to( $('.content, .media', el), { y:'-100vh', duration: frame * 0.45 }, 'section-'+i+'+='+(frame*0.55))
      .to( $('.content, .media', el), { opacity: 0, duration: frame * 0.05 }, 'section-'+i+'+='+(frame*0.95))
      
  }

  sectionEnterAwards(el, i, frame) {
    const leftCol = $('.column', el).first()
    const rightCol = $('.column', el).last()

    this.tl
      .from( $('.section-bg', el), {opacity: 0, duration: frame * 0.2 }, 'section-'+i )
      .from( $('img, p', leftCol), {opacity: 0, y: 50, duration: frame * 0.1, stagger: frame * 0.05 }, 'section-'+i+'+='+(frame*0.1) )
      .from( $('img, p', rightCol), {opacity: 0, y: 50, duration: frame * 0.1, stagger: frame * 0.05 }, 'section-'+i+'+='+(frame*0.3) )
      .to( [$('img, p', leftCol), $('img, p', rightCol)], { opacity: 0, y: 50, duration: frame * 0.05}, 'section-'+i+'+='+(frame*0.9) )
  }

  sectionEnterPhotos(el, i, frame) {
    this.tl
      .to( $('.section-bg', el), { opacity: 0, duration: frame * 0.1 }, 'section-'+i+'+='+(frame*0.9) )
  }

  sectionEnterLogos(el, i, frame) {
    this.tl
      .from( $('.logo', el), {opacity: 0, x: -50, y: 50, duration: frame * 0.1, stagger: frame * 0.02 }, 'section-'+i )
      .to( $('.logo', el), {opacity: 0, x: 50, y: 50, duration: frame * 0.1, stagger: frame * 0.01 }, 'section-'+i+'+='+(frame*0.85) )
  }

  sectionEnterDefault(el, i, frame) {
    this.tl.fromTo( el, {opacity: 0}, { opacity: 1, duration: frame * 0.45 }, 'section-'+i )
    if (!$(el).hasClass('anim-outtro')) {
      this.tl.to( el, { opacity: 0, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8) )
    }
  }

  /**
   * Video
   */

  videoEnter(el) {
    this.tlDirection === 1 ? this.videoPlay(el) : this.videoPause(el)
  }

  videoExit(el) {
    this.tlDirection === 1 ? this.videoPause(el) : this.videoPlay(el)
  }


  videoPlay(currentEl) {
    $('video', currentEl).each((i, el) => {
      el.currentTime = 0
      el.play()
    })
    $('.lottie-container', currentEl).each((i, el) => {
      $(el).data('instance').goToAndPlay( 0 )
    })
  }

  videoPause(currentEl) {
    $('video', currentEl).each((i, el) => {
      el.pause()
    })
    $('.lottie-container', currentEl).each((i, el) => {
      $(el).data('instance').goToAndStop( 0 )
    })
  }


  /**
   * Special animations
   */

  sectionQ2032(el, i, frame) {
    this.tl
      .from( $('.q2032-soccer-player', el), { opacity: 0, x:'-30vw', y:'-15vw', rotate:25, duration: frame * 0.4 }, 'section-'+i)
      .from( $('.q2032-soccer-ball', el), { opacity: 0, x:'5vw', y:'-50vw', duration: frame * 0.4 }, 'section-'+i)
      .from( $('.q2032-stadium', el), { opacity: 0, y:100, duration: frame * 0.2 }, 'section-'+i)
      .to( $('.q2032-soccer-player', el), { opacity: 0, x:'30vw', y:'10vw', rotate:-25, duration: frame * 0.4 }, 'section-'+i+'+='+(frame*0.6))
      .to( $('.q2032-soccer-ball', el), { opacity: 0, x:'50vw', y:'-5vw', duration: frame * 0.4 }, 'section-'+i+'+='+(frame*0.6))
      .to( $('.q2032-stadium', el), { opacity: 0, y:100, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8))
  }

  sectionQrc(el, i, frame) {
    this.tl
      .from( $('.qrc-corner', el), { opacity: 0, duration: frame * 0.2 }, 'section-'+i)
      .from( $('.qrc-corner-tr', el), { x:'25vw', y:'-25vw', duration: frame * 0.4 }, 'section-'+i)
      .from( $('.qrc-corner-bl', el), { x: '-25vw', y: '25vw', duration: frame * 0.4 }, 'section-'+i)
      .to( $('.qrc-corner-tr', el), { x:'25vw', y:'-25vw', duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.6))
      .to( $('.qrc-corner-bl', el), { x: '-25vw', y: '25vw', duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.6))
  }

  sectionRez(el, i, frame) {
    this.tl
      .from( $('.rez-bling', el), { opacity: 0, duration: frame * 0.4 }, 'section-'+i)
      .to( $('.rez-bling', el), { opacity: 0, duration: frame * 0.2 }, 'section-'+i+'+='+(frame*0.8))
  }


  /**
   * Funky DOM manipulation
   */

  splitHalves() {
    let sectionsToSplit = $('section.anim-halves', this.el)
    sectionsToSplit.each((i,el) => {
      let clone = $(el).removeClass('anim-halves').addClass('anim-halves-split').clone().insertAfter(el)
      $('.half', el).last().remove()
      $('.half', clone).first().remove()
    })
  }

}